import { Card } from '@atlaskit/media-card';
import type { Identifier, MediaClientConfig } from '@atlaskit/media-client';
import { token } from '@atlaskit/tokens';
import React, { useMemo } from 'react';
import styled from 'styled-components';

const THUMBNAIL_ATTACHMENT_SIZE = 32;
const BASE_MEDIA_SIZE = 250;
const VIEW_MEDIA_SIZING = 500;
const EDIT_MEDIA_SIZE = 100;

type AttachmentCardProps = {
  isThumbnail: boolean;
  attachmentLocation?: 'edit' | 'view';
  attachmentIdentifier: Identifier;
  shouldDisableOverlay: boolean;
  mediaClientConfig: MediaClientConfig;
};

const CardStylingOverrideContainer = styled.div`
  width: 100%;
  height: 100%;

  .new-file-experience-wrapper,
  .media-card-inline-player,
  .media-file-card-view {
    height: 100%;
    width: 100%;
  }

  .media-file-card-view > img,
  .media-card-inline-player > video {
    position: relative !important;
    left: unset !important;
    top: unset !important;
    transform: none !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
  }
`;

const DocumentCardContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: ${BASE_MEDIA_SIZE}px;

  [data-testid='media-card-view'],
  .media-card-frame {
    width: 100% !important;
    max-width: 100% !important;
    max-height: ${BASE_MEDIA_SIZE}px;
  }
`;

const ThumbnailContainer = styled.div`
  width: ${THUMBNAIL_ATTACHMENT_SIZE}px;
  height: ${THUMBNAIL_ATTACHMENT_SIZE}px;
  box-shadow: 0 0 0 1px ${token('color.border')};
  border-radius: ${token('radius.small', '4px')};

  .new-file-experience-wrapper,
  .media-card-inline-player {
    width: ${THUMBNAIL_ATTACHMENT_SIZE}px;
    height: ${THUMBNAIL_ATTACHMENT_SIZE}px;
  }
`;

export const AttachmentCard = ({
  isThumbnail,
  attachmentLocation,
  attachmentIdentifier,
  shouldDisableOverlay,
  mediaClientConfig,
}: AttachmentCardProps) => {
  const isAttachmentInViewMode = attachmentLocation === 'view';

  const mediaSize: number = isAttachmentInViewMode ? VIEW_MEDIA_SIZING : BASE_MEDIA_SIZE;

  const cardElement = useMemo(() => {
    const cardDimensions =
      isThumbnail && isAttachmentInViewMode
        ? { width: THUMBNAIL_ATTACHMENT_SIZE, height: THUMBNAIL_ATTACHMENT_SIZE }
        : attachmentLocation === 'edit'
        ? { width: EDIT_MEDIA_SIZE, height: EDIT_MEDIA_SIZE }
        : { width: mediaSize, height: mediaSize };
    return (
      <Card
        mediaClientConfig={mediaClientConfig}
        resizeMode={isThumbnail && isAttachmentInViewMode ? 'crop' : 'fit'}
        disableOverlay={shouldDisableOverlay}
        identifier={attachmentIdentifier}
        dimensions={cardDimensions}
        shouldHideTooltip
        useInlinePlayer
      />
    );
  }, [
    mediaSize,
    mediaClientConfig,
    shouldDisableOverlay,
    attachmentIdentifier,
    attachmentLocation,
    isAttachmentInViewMode,
    isThumbnail,
  ]);

  if (isAttachmentInViewMode && isThumbnail) {
    return <ThumbnailContainer>{cardElement}</ThumbnailContainer>;
  } else if (isAttachmentInViewMode && !isThumbnail) {
    if (shouldDisableOverlay) {
      return <CardStylingOverrideContainer>{cardElement}</CardStylingOverrideContainer>;
    } else {
      return <DocumentCardContainer>{cardElement}</DocumentCardContainer>;
    }
  } else {
    return cardElement;
  }
};
